import { FieldArray } from 'formik';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { array, number, object, string } from 'yup';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import BotaoLink from 'Component/BotaoLink';
import Cabecalho from 'Component/Cabecalho';
import Form, {
  Botao as BotaoForm,
  Campo,
  Contexto,
  Selecao,
} from 'Component/Form';
import Icone from 'Component/Icone';
import LayoutDefault from 'Component/Layout';
import FechamentoDeFolha from 'Interface/FechamentosDeFolha';
import Holerite from 'Interface/Holerites';
import Item from 'Interface/Itens';
import { ANOS, MESES, TIPOS_HOLERITE } from 'View/constants';
import { useRecuperarFechamentosDeFolhaQuery } from 'View/Service/Default/FechamentosDeFolha';
import { useAdicionarHoleriteMutation } from 'View/Service/Default/Holerites';
import { useRecuperarVinculosEmpregaticiosQuery } from 'View/Service/Default/VinculosEmpregaticios';

const regras = object({
  vinculo_empregaticio_id: number().required('Obrigatório'),
  ano: number().required('Obrigatório'),
  mes: number().required('Obrigatório'),
  tipo: number().required('Obrigatório'),
  salario_base: number().required('Obrigatório'),
  base_calculo_fgts: number().required('Obrigatório'),
  fgts_mes: number().required('Obrigatório'),
  base_calculo_inss: number().required('Obrigatório'),
  base_calculo_irrf: number().required('Obrigatório'),
  total_de_vencimentos: number().required('Obrigatório'),
  total_de_descontos: number().required('Obrigatório'),
  liquido: number().required('Obrigatório'),
  faixa_irrf: number().required('Obrigatório'),
  itens: array().of(
    object().shape({
      codigo: number().required('Obrigatório'),
      descricao: string().required('Obrigatório'),
      referencia: string().required('Obrigatório'),
      vencimentos: number().required('Obrigatório'),
      descontos: number().required('Obrigatório'),
    })
  ),
});

const Adicionar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();

  const { usuarioId = 0 } = useParams();

  const [mes, setMes] = useState<number>(DateTime.now().month);
  const [ano, setAno] = useState<number>(DateTime.now().year);
  const [adicionarHolerite] = useAdicionarHoleriteMutation();

  const [vencimentos, setVencimentos] = useState<number>(0);
  const [descontos, setDescontos] = useState<number>(0);
  const [liquido, setLiquido] = useState<number>(0);

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    data: { fechamentos_de_folha: [folha] } = {
      fechamentos_de_folha: [{} as FechamentoDeFolha],
    },
  } = useRecuperarFechamentosDeFolhaQuery({
    options: { ano, mes, usuario_id: usuarioId },
  });

  const { data: { vinculos } = {} } = useRecuperarVinculosEmpregaticiosQuery({
    finder: 'listaJson',
    contain: ['Empresas'],
    options: { usuario_id: usuarioId },
  });

  const enviar = async (dados: Holerite): Promise<void> => {
    try {
      await adicionarHolerite(dados).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });

      navegacao(`/usuarios/editar/${usuarioId}`);
    } catch (error) {
      toast({
        titulo: 'Erro ao adicionar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const calcularValores = (holerite: Holerite): void => {
    let vencimentos = 0;
    let descontos = 0;

    holerite?.itens?.forEach((item: Item) => {
      vencimentos += parseFloat(item.vencimentos.toString());
      descontos += parseFloat(item.descontos.toString());
    });

    setVencimentos(vencimentos);
    setDescontos(descontos);
    setLiquido(vencimentos - descontos);
  };

  // const inicial = useMemo((): Holerite => {
  //   const holerite: Partial<Holerite> = {};

  //   if (folha) {
  //     holerite.ano = ano;
  //     holerite.mes = mes;

  //     if (folha.salario) {
  //       holerite.salario_base = folha.salario;
  //     }

  //     holerite.itens = [];

  //     holerite.itens.push({
  //       codigo: '1',
  //       descricao: 'Salário do mês',
  //       referencia: '1',
  //       vencimentos: folha.salario,
  //       descontos: 0,
  //     });
  //     holerite.itens.push({
  //       codigo: '8',
  //       descricao: 'Salário Comissões mês',
  //       referencia: '1',
  //       vencimentos: folha.valor,
  //       descontos: 0,
  //     });
  //     holerite.itens.push({
  //       codigo: '2314',
  //       descricao: 'Prêmio por Metas',
  //       referencia: '1',
  //       vencimentos: parseFloat(folha.premio_de_metas_vendedores) + parseFloat(folha.premio_de_metas_geral),
  //       descontos: 0,
  //     });
  //     holerite.itens.push({
  //       codigo: '7504',
  //       descricao: 'Adiantamentos no mês',
  //       referencia: '1',
  //       vencimentos: 0,
  //       descontos: folha.adiantamento,
  //     });
  //     holerite.itens.push({
  //       codigo: '8101',
  //       descricao: 'Vale Transporte',
  //       referencia: '1',
  //       vencimentos: 0,
  //       descontos: folha.vale_transporte,
  //     });
  //     holerite.itens.push({
  //       codigo: '8327',
  //       descricao: 'Parc Emprest Consignacao',
  //       referencia: '1',
  //       vencimentos: 0,
  //       descontos: folha.consignado,
  //     });
  //     holerite.itens.push({
  //       codigo: '7704',
  //       descricao: 'Convenio Saude',
  //       referencia: '1',
  //       vencimentos: 0,
  //       descontos: folha.medico,
  //     });
  //     holerite.itens.push({
  //       codigo: '7724',
  //       descricao: 'Convenio de Farmacia',
  //       referencia: '1',
  //       vencimentos: 0,
  //       descontos: folha.farmacia,
  //     });
  //     holerite.itens.push({
  //       codigo: '7703',
  //       descricao: 'Convenio Odontológico',
  //       referencia: '1',
  //       vencimentos: 0,
  //       descontos: folha.farmacia,
  //     });

  //     holerite.total_de_descontos = 0;
  //     holerite.total_de_vencimentos = 0;

  //     holerite.itens.map(item => {
  //       holerite.total_de_descontos += parseFloat(item.descontos);
  //       holerite.total_de_vencimentos += parseFloat(item.vencimentos);
  //     })
  //   }

  //   return holerite as Holerite;
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [folha]);

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Adicionando holerite"
        descricao="Preencha o formulário e clique em enviar para adicionar o holerite"
      />
      <Form onSubmit={enviar} regras={regras}>
        <div className="row justify-content-center">
          <div className="col-md-12">
            <div className="bg-white shadow-sm rounded p-4">
              <div className="row">
                <div className="col-md-12">
                  <Selecao
                    name="vinculo_empregaticio_id"
                    rotulo="Vinculo empregatício"
                    opcoes={vinculos}
                    autoSelecao
                  />
                </div>
                <div className="col-md-4">
                  <Selecao name="tipo" rotulo="Tipo" opcoes={TIPOS_HOLERITE} />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="mes"
                    rotulo="Mês"
                    opcoes={MESES}
                    aoAtualizar={setMes}
                  />
                </div>
                <div className="col-md-4">
                  <Selecao
                    name="ano"
                    rotulo="Ano"
                    opcoes={ANOS}
                    aoAtualizar={setAno}
                  />
                </div>
                <div className="col-md-12 border-top border-bottom my-3 pt-3">
                  <Contexto>
                    {({ values }) => (
                      <FieldArray name="itens">
                        {({ remove, push }) => (
                          <React.Fragment>
                            {values?.itens.length > 0 &&
                              values?.itens?.map(
                                (item: Item, indice: number) => (
                                  <div
                                    key={indice.toString()}
                                    className={`row ${
                                      indice && 'border-top pt-3'
                                    }`}
                                  >
                                    <div className="col-md-11">
                                      <div className="row">
                                        <div className="col-md-1">
                                          <Campo
                                            name={`itens.${indice}.codigo`}
                                            rotulo="Código"
                                          />
                                        </div>
                                        <div className="col-md-5">
                                          <Campo
                                            name={`itens.${indice}.descricao`}
                                            rotulo="Descrição"
                                          />
                                        </div>
                                        <div className="col-md-2">
                                          <Campo
                                            name={`itens.${indice}.referencia`}
                                            rotulo="Referência"
                                          />
                                        </div>
                                        <div className="col-md-2">
                                          <Campo
                                            name={`itens.${indice}.vencimentos`}
                                            rotulo="Vencimentos"
                                          />
                                        </div>
                                        <div className="col-md-2">
                                          <Campo
                                            name={`itens.${indice}.descontos`}
                                            rotulo="Descontos"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-1">
                                      <Botao
                                        tema="danger"
                                        icone="trash-fill"
                                        onClick={() => remove(indice)}
                                      />
                                    </div>
                                  </div>
                                )
                              )}
                            <div className="col-md-12 mb-3 d-flex justify-content-center gap-2">
                              <Botao
                                tema="success"
                                icone="plus"
                                onClick={() =>
                                  push({
                                    codigo: '',
                                    descricao: '',
                                    referencia: '',
                                    vencimentos: '',
                                    descontos: '',
                                  })
                                }
                              >
                                Adicionar item
                              </Botao>
                              {values.itens && (
                                <Botao
                                  onClick={() =>
                                    calcularValores(values as Holerite)
                                  }
                                >
                                  Calcular valores
                                </Botao>
                              )}
                            </div>
                          </React.Fragment>
                        )}
                      </FieldArray>
                    )}
                  </Contexto>
                </div>
                <div className="col-md-4">
                  <Campo
                    name="salario_base"
                    rotulo="Salário base"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="base_calculo_fgts"
                    rotulo="Base calc. FGTS"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="fgts_mes"
                    rotulo="FGTS do mês"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="base_calculo_inss"
                    rotulo="Base calc. INSS"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="base_calculo_irrf"
                    rotulo="Base calc. IRRF"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="total_de_vencimentos"
                    rotulo="Total de vencimentos"
                    prefixar={<Icone nome="currency-dollar" />}
                    forcarValor={vencimentos}
                    desabilitarAoForcar={false}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="total_de_descontos"
                    rotulo="Total de descontos"
                    prefixar={<Icone nome="currency-dollar" />}
                    forcarValor={descontos}
                    desabilitarAoForcar={false}
                  />
                </div>
                <div className="col-md-4">
                  <Campo
                    name="liquido"
                    rotulo="Valor líquido"
                    prefixar={<Icone nome="currency-dollar" />}
                    forcarValor={liquido}
                    desabilitarAoForcar={false}
                  />
                </div>
                <div className="col-md-4">
                  <Campo name="faixa_irrf" rotulo="Faixa IRRF" />
                </div>
              </div>

              <div className="mt-5">
                <BotaoForm>Enviar</BotaoForm>
                <BotaoLink
                  tema="light"
                  to={`/usuarios/editar/${usuarioId}`}
                  className="ms-2"
                >
                  Cancelar
                </BotaoLink>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </LayoutDefault>
  );
};

export default Adicionar;
